import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setTheme } from "@/lib/theme";
import { toast } from "@/components/ui/sonner";
import { Eyebrow } from "@/components/Eyebrow";
import { Check, ArrowRight, ShieldCheck, FileSignature, Sparkles, Loader2 } from "lucide-react";

const ACCENT = "#FF6B2C";

const TIERS = [
  {
    tier: "Starter",
    pitch: "Everything you need to put one brand on autopilot.",
    priceMonthly: "$249",
    priceNote: "billed monthly",
    allowance: "AI allowance — up to 40 generated posts / month",
    features: [
      "1 brand workspace + Brand DNA",
      "Competitor & industry research",
      "On-brand posts for Facebook, Instagram & TikTok",
      "Swipe-to-approve + plain-language revisions",
      "Calendar scheduling",
    ],
    cta: "Start with Starter",
    featured: false,
  },
  {
    tier: "Pro",
    pitch: "A whole month of content, ready for your approval.",
    priceMonthly: "$499",
    priceNote: "billed monthly",
    allowance: "Standard AI allowance — up to 120 generated posts / month",
    featuresLead: "Everything in Starter, plus:",
    features: [
      "Up to 10 brand workspaces",
      "Autopilot batch generation",
      "Content pillars & angle library",
      "Export assets & captions",
    ],
    cta: "Start with Pro",
    featured: true,
  },
  {
    tier: "Enterprise",
    pitch: "Higher AI allowance and priority support for teams running multiple brands.",
    priceMonthly: "$1,499",
    priceNote: "billed monthly",
    allowance: "Expanded AI allowance — up to 500 generated posts / month",
    featuresLead: "Everything in Pro, plus:",
    features: [
      "Unlimited brand workspaces",
      "Priority generation & support",
      "Team seats & approval workflow",
      "Dedicated HeyDesignGuy strategist on-call",
    ],
    cta: "Get started with Enterprise",
    featured: false,
  },
];

const REASSURANCE = [
  { icon: FileSignature, text: "No long-term contracts — cancel anytime" },
  { icon: ShieldCheck, text: "Secure API key handling — your accounts stay protected" },
  { icon: Sparkles, text: "Built on real brand work — agency-caliber, not generic filler" },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cfg, setCfg] = useState({ poweredByUrl: "https://heydesignguy.com" });
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    setTheme("workspace");
    api.get("/public-config").then(({ data }) => setCfg((c) => ({ ...c, ...data }))).catch(() => {});
  }, []);

  const startCheckout = async (tier) => {
    if (!user) {
      toast(`Sign in to start your ${tier} plan`, { description: "Create or open your workspace to continue to checkout." });
      navigate("/login");
      return;
    }
    setBusy(tier);
    try {
      const { data } = await api.post("/payments/checkout", { tier, originUrl: window.location.origin });
      if (data.url) window.location.href = data.url;
      else throw new Error("No checkout URL");
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout"));
      setBusy(null);
    }
  };

  return (
    <div data-testid="pricing-page" className="lightboard" style={{ minHeight: "100vh", background: "var(--surface)", position: "relative" }}>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "radial-gradient(circle, #E4E4E7 1px, transparent 1px)", backgroundSize: "26px 26px", opacity: 0.4 }} />

      {/* top bar */}
      <header style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", maxWidth: 1120, margin: "0 auto" }}>
        <button data-testid="pricing-home" onClick={() => navigate("/")} aria-label="HDG Creative Studio — home" style={{ display: "flex", alignItems: "center", gap: 11, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          <img src="/hdg-logo.jpg" alt="HeyDesignGuy" style={{ height: 36, width: 36, objectFit: "contain", borderRadius: 10, background: "#fff", border: "1px solid var(--cardborder)" }} />
          <span className="font-display" style={{ fontWeight: 700, fontSize: 15.5, color: "var(--tprimary)", letterSpacing: "-0.01em" }}>HeyDesignGuy</span>
        </button>
        <button data-testid="pricing-signin" onClick={() => navigate("/login")} className="btn-secondary font-mono" style={{ fontSize: 12, padding: "9px 16px" }}>
          Sign in
        </button>
      </header>

      <main style={{ position: "relative", zIndex: 2, maxWidth: 1000, margin: "0 auto", padding: "32px 24px 80px" }}>
        {/* page header */}
        <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 48px" }}>
          <div style={{ display: "flex", justifyContent: "center" }}><Eyebrow testId="pricing-eyebrow">Pricing</Eyebrow></div>
          <h1 className="font-display" style={{ fontSize: 44, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.08, color: "var(--tprimary)", margin: "16px 0 14px" }}>
            One platform. Your brand on autopilot.
          </h1>
          <p className="font-body" style={{ fontSize: 16.5, color: "var(--tsecondary)", lineHeight: 1.55, margin: 0 }}>
            Pick the AI allowance that matches your growth. Every plan starts with a <strong style={{ color: "var(--tprimary)" }}>14-day free trial</strong> — cancel anytime.
          </p>
        </div>

        {/* cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ alignItems: "stretch" }}>
          {TIERS.map((t) => (
            <div
              key={t.tier}
              data-testid={`pricing-card-${t.tier.toLowerCase()}`}
              className="panel-card"
              style={{
                position: "relative", padding: 30, display: "flex", flexDirection: "column",
                background: "#fff",
                border: t.featured ? `2px solid ${ACCENT}` : "1px solid var(--cardborder)",
                boxShadow: t.featured ? "0 18px 50px rgba(255,107,44,0.16)" : undefined,
              }}
            >
              {t.featured && (
                <span data-testid="pricing-badge-popular" className="font-mono" style={{ position: "absolute", top: -13, left: 30, background: ACCENT, color: "#fff", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "5px 12px", borderRadius: 999 }}>
                  Most popular
                </span>
              )}

              <h2 className="font-display" style={{ fontSize: 22, fontWeight: 700, color: "var(--tprimary)", margin: 0 }}>{t.tier}</h2>
              <p className="font-body" style={{ fontSize: 14, color: "var(--tsecondary)", lineHeight: 1.5, margin: "8px 0 20px", minHeight: 42 }}>{t.pitch}</p>

              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span className="font-display" style={{ fontSize: 40, fontWeight: 700, color: "var(--tprimary)", letterSpacing: "-0.02em" }}>{t.priceMonthly}</span>
                <span className="font-body" style={{ fontSize: 14, color: "var(--tmuted)" }}>/mo</span>
              </div>
              <div className="font-body" style={{ fontSize: 12.5, color: "var(--tmuted)", marginTop: 2 }}>{t.priceNote}</div>

              <div style={{ marginTop: 18, padding: "12px 14px", borderRadius: 10, background: "var(--surface)", border: "1px solid var(--cardborder)" }}>
                <span className="font-body" style={{ fontSize: 13, color: "var(--tprimary)", fontWeight: 600, lineHeight: 1.45 }}>{t.allowance}</span>
              </div>

              <ul style={{ listStyle: "none", margin: "22px 0 24px", padding: 0, display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
                {t.featuresLead && (
                  <li className="font-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--tprimary)" }}>{t.featuresLead}</li>
                )}
                {t.features.map((f, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <Check size={16} strokeWidth={3} style={{ color: ACCENT, flexShrink: 0, marginTop: 1 }} />
                    <span className="font-body" style={{ fontSize: 13.5, color: "var(--tsecondary)", lineHeight: 1.45 }}>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`pricing-${t.tier.toLowerCase()}-cta`}
                onClick={() => startCheckout(t.tier)}
                disabled={busy === t.tier}
                className="font-body"
                style={{
                  width: "100%", padding: "13px 0", borderRadius: 10, border: "none", cursor: "pointer",
                  background: ACCENT, color: "#fff", fontWeight: 700, fontSize: 14.5,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  opacity: busy === t.tier ? 0.7 : 1, transition: "opacity 0.2s ease, transform 0.15s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
              >
                {busy === t.tier ? <Loader2 size={16} className="spin" /> : null}
                {t.cta} {busy !== t.tier && <ArrowRight size={16} />}
              </button>
            </div>
          ))}
        </div>

        {/* reassurance row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="pricing-reassurance" style={{ marginTop: 44 }}>
          {REASSURANCE.map(({ icon: Icon, text }, i) => (
            <div key={i} data-testid={`pricing-reassure-${i}`} style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
              <Icon size={18} style={{ color: "var(--tsecondary)", flexShrink: 0, marginTop: 1 }} />
              <span className="font-body" style={{ fontSize: 13.5, color: "var(--tsecondary)", lineHeight: 1.45 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* footer CTA */}
        <div data-testid="pricing-footer-cta" style={{ textAlign: "center", marginTop: 54, padding: "28px 24px", borderRadius: 14, border: "1px solid var(--cardborder)", background: "#fff" }}>
          <p className="font-body" style={{ fontSize: 15.5, color: "var(--tprimary)", margin: 0, lineHeight: 1.55 }}>
            Not sure which fits?{" "}
            <a data-testid="pricing-book-call" href={cfg.poweredByUrl} target="_blank" rel="noreferrer" className="font-body" style={{ color: ACCENT, fontWeight: 700, textDecoration: "underline", textUnderlineOffset: 3 }}>
              Book a call with HeyDesignGuy
            </a>{" "}
            — we'll map your first month of content free.
          </p>
        </div>
      </main>
    </div>
  );
}
